/*
 * Column preferences the CMMS grids remember for the user: order, width, and which columns are
 * shown at all.
 *
 * The three grids each carried their own byte-equivalent copy of this until a review found the
 * same two defects sitting in all three. One copy is easier to fix than three, and unlike a grid
 * it can be tested.
 */

/*
 * Reading a preference back out of localStorage.
 *
 * We write every one of these keys ourselves, so a value that will not parse means something
 * outside the app reached it. Rare — but the read happens inside a render, so the exception takes
 * the page down, and it keeps taking it down, because the bad value is still there on the next
 * load. Dropping the key breaks that loop. The cost is one forgotten preference.
 */
export const readStoredJson = (key, fallback) => {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;

  try {
    return JSON.parse(raw);
  } catch {
    localStorage.removeItem(key);
    return fallback;
  }
};

export const reorderColumns = (orderArray, columnsToFix) => {
  const byField = new Map(columnsToFix.map((col) => [col.field, col]));
  const ordered = orderArray.map((field) => byField.get(field)).filter(Boolean);
  const seen = new Set(ordered);

  /*
   * A column the saved order never mentions belongs where the base definition put it, not on the
   * end.
   *
   * Orders written before the getAllColumns fix omit every hidden column, and appending them is
   * exactly what put `id` to the right of Edit/Delete once the Columns panel could unhide it.
   * Restoring by index repairs those stored orders rather than only stopping new bad ones, and it
   * gives a column added in a later story its intended position for existing users too.
   */
  columnsToFix.forEach((col, index) => {
    if (!seen.has(col)) ordered.splice(index, 0, col);
  });

  return ordered;
};

/*
 * The visibility model a grid starts with: the stored one laid over the grid's defaults.
 *
 * A stored model only names the columns that existed when it was written. Used verbatim, it shows
 * every column added since, whatever that column's default says. Laying it over the defaults keeps
 * every choice the user made and gives a newly added column its default for them too.
 */
export const readColumnVisibility = (key, defaults) => ({ ...defaults, ...readStoredJson(key, {}) });

/*
 * The visibility model to store: every column named, shown or not.
 *
 * The grid's "Show all" deletes keys rather than setting them to true, and a missing key reads
 * back through readColumnVisibility as the default, so a default-hidden column would hide again
 * on the next load. Naming every column makes the stored model mean exactly what was on screen.
 */
export const toStoredColumnVisibility = (model, fields) =>
  Object.fromEntries(fields.map((field) => [field, model?.[field] !== false]));

export const applyColumnWidths = (widthMap, columnsToFix) =>
  columnsToFix.map((col) => ({
    ...col,
    width: widthMap[col.field] || col.width,
    flex: widthMap[col.field] !== undefined ? undefined : col.flex,
  }));
